<template>
  <Breadcrumb :items="breadcrumbItems" />

  <div class="header-menu-acoes">
    <h2 class="titulo-pagina">Ações de Extensão</h2>
    <div class="header-actions">
      <button class="btn-toggle-encerradas" @click="exibirEncerradas = !exibirEncerradas"
        :class="{ ativa: exibirEncerradas }">
        <i class="ti ti-archive" v-if="!exibirEncerradas"></i>
        <i class="ti ti-list" v-else></i>
        <span>{{ exibirEncerradas ? "Ver ações ativas" : "Ver ações encerradas" }}</span>
      </button>

      <FiltrosComponent :nomeTela="'acao'" :isCoordenador="false" :filtrosIniciais="{ unidadeId: filtroUnidadeId }"
        @aplicarFiltros="onAplicarFiltros" @pesquisar="onPesquisar" />
    </div>
  </div>

  <Carregamento v-if="loading" />

  <div v-else-if="!acaoSelecionada">

    <!-- MINHAS AÇÕES -->
    <div class="secao-acoes" v-if="!estaPesquisando && isLogado">
      <h3 class="subtitulo-secao">Minhas Ações</h3>

      <div v-if="acoesFiltradasMinhas.length > 0" class="grid-acoes">
        <div class="conteudo" v-for="acao in acoesFiltradasMinhas" :key="acao.acao_id || acao.id"
          @click="abrirAcao(acao)" @keyup.enter="abrirAcao(acao)" tabindex="0" role="button"
          aria-label="Abrir detalhes da ação">
          <div class="imagem-conteudo-acao">
            <img v-if="acao.imagemUrl" :src="acao.imagemUrl" alt="Imagem da Ação" loading="lazy" decoding="async" />
            <img v-else src="@/assets/logoGex1.2.png" alt="Logo GEX" loading="lazy" decoding="async" />
          </div>
          <div class="conteudo-listagem-acao">
            <div class="cima-conteudo-acao">
              <h5 class="titulo-acao">
                {{ acao.titulo }}
              </h5>
              <span v-if="acao.dataInicio || acao.dataFim" class="periodo-acao">
                <b>Período: </b>
                {{ formatarData(acao.dataInicio) }} a {{ formatarData(acao.dataFim) }}
              </span>
              <p class="descricao-texto">
                <b>Descrição: </b>
                {{ acao.descricao || "Sem descrição informada." }}
              </p>
            </div>
            <div class="baixo-conteudo-acao">
              <p class="area-tematica">
                <b>Área Temática: </b>
                {{ acao.areaTematica || "Não informada" }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="sem-acoes">
        <p>Nenhuma ação {{ exibirEncerradas ? 'encerrada' : 'ativa' }} encontrada.</p>
      </div>
    </div>

    <!-- OUTRAS AÇÕES / TODAS AS AÇÕES -->
    <div class="secao-acoes" :style="isLogado && !estaPesquisando ? 'margin-top: 40px' : ''">
      <h3 class="subtitulo-secao">{{ tituloOutrasAcoes }}</h3>

      <div v-if="acoesFiltradasOutras.length > 0" class="grid-acoes">
        <div class="conteudo" v-for="acao in acoesFiltradasOutras" :key="'outras-' + (acao.acao_id || acao.id)"
          @click="abrirAcao(acao)" @keyup.enter="abrirAcao(acao)" tabindex="0" role="button">
          <div class="imagem-conteudo-acao">
            <img v-if="acao.imagemUrl" :src="acao.imagemUrl" alt="Imagem da Ação" loading="lazy" decoding="async" />
            <img v-else src="@/assets/logoGex1.2.png" alt="Logo GEX" loading="lazy" decoding="async" />
          </div>
          <div class="conteudo-listagem-acao">
            <div class="cima-conteudo-acao">
              <h5 class="titulo-acao">
                {{ acao.titulo }}
              </h5>
              <span v-if="acao.dataInicio || acao.dataFim" class="periodo-acao">
                <b>Período:</b> 
                {{ formatarData(acao.dataInicio) }} a {{ formatarData(acao.dataFim) }}
              </span>
              <p class="descricao-texto">
                <b>Descrição: </b>
                {{ acao.descricao || "Sem descrição informada." }}
              </p>
            </div>
            <div class="baixo-conteudo-acao">
              <p class="area-tematica">
                <b>Área Temática: </b>
                {{ acao.areaTematica || "Não informada" }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="sem-acoes">
        <p>
          Nenhuma {{ isLogado && !estaPesquisando ? 'outra ' : '' }}
          ação {{ exibirEncerradas ? 'encerrada' : 'ativa' }} encontrada.
        </p>
      </div>

      <div v-if="totalOutrasPaginas > 1">
        <PaginacaoComponent :total-paginas="totalOutrasPaginas" :pagina-atual="paginaOutras"
          @mudar-pagina="mudarPaginaOutras" />
      </div>
    </div>
  </div>

  <div v-if="acaoSelecionada">
    <AcaoView @atualizarTitulo="emit('paginaAberta', true)" />
  </div>

  <ModalEditarSigla :mostrar="modalSiglaVisivel" :acaoId="acaoSelecionadaParaEdicao?.acao_id"
    :siglaAtual="acaoSelecionadaParaEdicao?.sigla" :corAtual="acaoSelecionadaParaEdicao?.corSigla"
    @fechar="fecharModalSigla" @atualizado="buscarAcoes" />
</template>

<script setup>
import { ref, computed, watchEffect, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import AcaoView from "@/views/acoes/AcaoView.vue";
import ModalEditarSigla from "@/components/acoes/ModalEditarSigla.vue";
import FiltrosComponent from "@/components/common/FiltrosComponent.vue";
import Carregamento from "@/components/common/Carregamento.vue";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import PaginacaoComponent from "@/components/common/paginacaoComponent.vue";
import usuariosService from "@/service/usuariosService";
import acaoService from "@/service/acaoService";
import { formatarData } from "@/utils/FormatarEntradas.js";
import userInfos from "@/utils/userInfos";
import { useAuthStore } from "@/stores/auth";

defineOptions({ name: "MenuAcoesView" });

const emit = defineEmits(["paginaAberta"]);
const props = defineProps({
  isAtiva: Boolean,
});

const authStore = useAuthStore();
const isLogado = computed(() => authStore.isAutenticado);

const loading = ref(false);
const loadingMais = ref(false);
const router = useRouter();
const route = useRoute();

const exibirEncerradas = ref(false);
const acaoSelecionada = ref(false);
const acaoAtiva = ref(null);

const minhasAcoes = ref([]);
const outrasAcoes = ref([]);

const paginaOutras = ref(Number(route.query.page) || 0);
const tamanhoPagina = 10;
const totalOutrasPaginas = ref(1);

const filtroUnidadeId = ref(null);
const termoBusca = ref("");

const modalSiglaVisivel = ref(false);
const acaoSelecionadaParaEdicao = ref(null);

const breadcrumbItems = computed(() => [
  { label: "Início", to: "/" },
  { label: "Ações", to: "/suas-acoes" },
]);

watchEffect(() => {
  if (!props.isAtiva) {
    acaoSelecionada.value = false;
    emit("paginaAberta", false);
  }
});

watch(
  () => route.query.page,
  (newPage) => {
    const pagina = Number(newPage) || 0;
    paginaOutras.value = pagina;
    buscarOutrasAcoes(pagina);
  }
);

watch(exibirEncerradas, () => {
  paginaOutras.value = 0;
  buscarOutrasAcoes(0);
  router.replace({ query: { ...route.query, page: 0 } }).catch(() => { });
});

function abrirModalSigla(acao) {
  acaoSelecionadaParaEdicao.value = acao;
  modalSiglaVisivel.value = true;
}

function fecharModalSigla() {
  modalSiglaVisivel.value = false;
  acaoSelecionadaParaEdicao.value = null;
}

function abrirAcao(acao) {
  const idAcao = acao.acao_id || acao.id;
  acaoAtiva.value = acao;
  acaoSelecionada.value = true;
  emit("paginaAberta", true);
  router.push({ name: "acao-default", params: { id: idAcao } });
}

function isEncerrada(acao) {
  if (acao.status === "ENCERRADA" || acao.encerrada === true || acao.ativo === false) return true;
  if (acao.dataFim) return new Date(acao.dataFim) < new Date();
  return false;
}

const acoesFiltradasMinhas = computed(() =>
  minhasAcoes.value.filter(a => exibirEncerradas.value ? isEncerrada(a) : !isEncerrada(a))
);

const acoesFiltradasOutras = computed(() =>
  outrasAcoes.value.filter(a => exibirEncerradas.value ? isEncerrada(a) : !isEncerrada(a))
);

const estaPesquisando = computed(() => !!termoBusca.value || !!filtroUnidadeId.value);

const tituloOutrasAcoes = computed(() => {
  if (isLogado.value && !estaPesquisando.value) {
    return exibirEncerradas.value ? "Outras Ações Encerradas" : "Outras Ações";
  }
  return exibirEncerradas.value ? "Ações Encerradas" : "Todas as Ações de Extensão";
});

async function buscarMinhasAcoes() {
  try {
    const acoesUsuario = await usuariosService.getAcoesUsuario();
    if (acoesUsuario && Array.isArray(acoesUsuario)) {
      const userName = userInfos.getUserInfos()?.nome || "";
      minhasAcoes.value = acoesUsuario
        .map((acao) => ({ ...acao, titulo: acao.titulo?.replace(/"/g, "") || "Sem título" }))
        .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || "", "pt-BR", { sensitivity: "base" }));
    }
  } catch (error) {
    console.error("Erro ao buscar minhas ações:", error);
  }
}

async function buscarOutrasAcoes(pagina = 0) {
  loadingMais.value = true;
  try {
    const response = await acaoService.getAcoesFiltro(
      pagina,
      tamanhoPagina,
      filtroUnidadeId.value,
      exibirEncerradas.value ? "ENCERRADA" : "ATIVA",
      termoBusca.value || null
    );
    if (response?.content) {
      outrasAcoes.value = response.content.map(acao => ({
        ...acao,
        titulo: acao.titulo?.replace(/"/g, "") || "Sem título"
      }));
      totalOutrasPaginas.value = response.totalPages || 1;
      paginaOutras.value = response.number || pagina;
    }
  } catch (error) {
    console.error("Erro ao buscar outras ações:", error);
  } finally {
    loadingMais.value = false;
  }
}

function mudarPaginaOutras(pagina) {
  router.push({ query: { ...route.query, page: pagina } });
}

async function buscarAcoes() {
  loading.value = true;
  if (isLogado.value) {
    await buscarMinhasAcoes();
  } else {
    minhasAcoes.value = [];
  }
  await buscarOutrasAcoes(paginaOutras.value);
  loading.value = false;
}

function onAplicarFiltros(filtros) {
  filtroUnidadeId.value = filtros.unidadeId || null;
  resetarEPesquisar();
}

function onPesquisar(texto) {
  termoBusca.value = texto;
  resetarEPesquisar();
}

function resetarEPesquisar() {
  paginaOutras.value = 0;
  buscarOutrasAcoes(0);
  router.replace({ query: { ...route.query, page: 0 } }).catch(() => { });
  window.scrollTo({ top: 0, behavior: "smooth" });
}

onMounted(buscarAcoes);
</script>

<style scoped>
.titulo-pagina {
  padding-bottom: 0;
  margin: 0;
}

.subtitulo-secao {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--cinza-escuro);
}

.sem-acoes {
  padding: 20px;
  background: var(--branco);
  border-radius: 8px;
  text-align: center;
  color: var(--cinza-escuro);
}

.sem-acoes p {
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
}

.header-menu-acoes {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 15px;
  border-bottom: 1px solid lightgray;
  width: 100%;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
}

.header-actions :deep(.barra-acoes) {
  border-bottom: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 16px !important;
}

.btn-toggle-encerradas {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 14px;
  height: 39px;
  background: var(--branco);
  color: var(--roxo-primario);
  border: 1px solid var(--roxo-primario);
  border-radius: 5px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
  line-height: 1.2;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-toggle-encerradas:hover {
  background-color: var(--roxo-superficie);
}

.btn-toggle-encerradas.ativa {
  background-color: var(--roxo-primario);
  color: var(--branco);
  border-color: var(--roxo-primario);
}

.btn-toggle-encerradas i {
  font-size: 18px;
  display: flex;
  align-items: center;
}

.grid-acoes {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.conteudo {
  display: flex;
  flex-direction: row;
  box-shadow: none;
  background-color: transparent;
  border: 1px solid var(--cinza-medio);
  border-radius: 0.5rem;
  cursor: pointer;
  padding: 24px 20px;
  transition: transform 200ms, box-shadow 200ms;
  width: 100%;
  box-sizing: border-box;
}



.conteudo p {
  font-size: 15px;
  color: var(--preto);
  margin: 0;
}

.conteudo:hover {
  transform: translateY(-2px);
  border-color: var(--roxo-claro);
  box-shadow:
    0 10px 24px -3px hsla(258, 53%, 28%, 0.14),
    0 4px 10px -2px hsla(258, 53%, 28%, 0.06);
}

.imagem-conteudo-acao {
  width: 260px;
  min-width: 260px;
  height: 170px;
  flex-shrink: 0;
}

.imagem-conteudo-acao img {
  height: 100%;
  width: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.conteudo-listagem-acao {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  padding: 0 0 0 20px;
  flex: 1;
  min-width: 0;
}

.cima-conteudo-acao h5.titulo-acao {
  margin: 0 0 4px 0;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.periodo-acao {
  display: block;
  font-size: 0.85rem;
  color: var(--cinza-escuro);
  margin-bottom: 12px;
}

.descricao-texto {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.5;
  margin-bottom: 12px;
}

.area-tematica {
  font-size: 15px;
  color: var(--cinza-escuro);
}

.periodo-acao b,
.descricao-texto b,
.area-tematica b {
  color: var(--roxo-primario);
}

@media (max-width: 900px) {
  .conteudo {
    padding: 18px;
  }

  .imagem-conteudo-acao {
    width: 220px;
    min-width: 220px;
    height: 150px;
  }
}

@media (max-width: 600px) {
  .header-menu-acoes {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .titulo-pagina {
    font-size: 1.5rem;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-toggle-encerradas {
    width: 100%;
  }

  .header-actions :deep(.barra-acoes) {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .grid-acoes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }

  .conteudo {
    flex-direction: column;
    padding: 0;
    gap: 0;
    height: 100%;
    overflow: hidden;
  }

  .conteudo:hover {
    transform: none;
  }

  .imagem-conteudo-acao {
    width: 100%;
    min-width: 100%;
    height: 160px;
  }

  .imagem-conteudo-acao img {
    border-radius: 0;
  }

  .conteudo-listagem-acao {
    padding: 16px;
    flex: 1;
  }

  .cima-conteudo-acao h5.titulo-acao {
    font-size: 1.1rem;
    margin-bottom: 6px;
  }

  .periodo-acao {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }

  .descricao-texto {
    font-size: 13.5px !important;
    -webkit-line-clamp: 3 !important;
  }

  .area-tematica {
    font-size: 13px !important;
    margin-top: auto;
  }
}
</style>